import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { MyHandler } from "../component";
import Cookies from "universal-cookie";
import axios from "axios";

const HandlerModal = ({
  open,
  setOpen,
  handler,
  onSaved,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  handler: MyHandler | null;
  onSaved: () => void;
}): JSX.Element => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (handler) {
      setFirstName(handler.first_name);
      setLastName(handler.last_name);
    } else {
      setFirstName("");
      setLastName("");
    }
    setError(null);
  }, [handler, open]);

  const isNew = !handler?.id;

  const handleSave = async () => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) return;

    try {
      setSaving(true);
      setError(null);
      const token = new Cookies().get("token");
      const payload: Record<string, unknown> = {
        first_name: trimmedFirst,
        last_name: trimmedLast,
      };
      if (!isNew) {
        payload.id = handler!.id;
      }

      await axios.post("https://api.easyagility.co.uk/my-handlers", payload, {
        headers: { Authorization: "Bearer " + token },
      });

      setOpen(false);
      onSaved();
    } catch (err) {
      console.error("Error saving handler:", err);
      setError("Failed to save handler. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!saving) setOpen(false);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: isMobile ? "100%" : "90%",
          maxWidth: 480,
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.primary.main,
            color: "white",
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {isNew ? "Add New Handler" : "Edit Handler"}
          </Typography>
          {!isNew && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {handler?.full_name}
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2.5}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              variant="outlined"
              disabled={saving}
              autoFocus
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              variant="outlined"
              disabled={saving}
            />
          </Stack>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none" }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !firstName.trim() || !lastName.trim()}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default HandlerModal;
